import { CheckCircleFilled, ExclamationCircleFilled } from '@ant-design/icons';
import { Button, Form, Skeleton, Spin, Typography } from 'antd';
import { useState } from 'react';

import { Phone } from '@app/components/Phone';
import type { PhoneState } from '@app/components/Phone/types';
import { PNG } from '@app/constants/fileMimeTypes';
import { useUserPrint } from '@app/hooks';

import { useWhatsAppPairingCode } from '@pages/Integrations/IntegrationsList/components/WhatsAppAuthModalContent/hooks/useWhatsAppPairingCode';

import { WhatsAppAuthPhase } from '@pages/Integrations/IntegrationsList/lib/whatsAppAuthPhase';

import type { FC } from 'react';

import '@pages/Integrations/IntegrationsList/components/WhatsAppAuthModalContent/styles/WhatsAppAuthModalContent.desktop.scss';

const { Text, Link } = Typography;
const { Item, useForm } = Form;

export interface WhatsAppAuthModalContentProps {
  qr?: string;
  integrationId: number | null;
  activeBranchId: number | null;
  /** Фаза сессии авторизации: код, завершение, успех или ошибка (issue/11029). */
  phase?: WhatsAppAuthPhase;
  errorMessage?: string;
  /** Повторить вход после ошибки. */
  onRetry?: () => void;
}

interface PhoneFormValues {
  phone?: PhoneState;
}

// Страна по умолчанию — как в модалке записи (RecordDetails): страна
// активного филиала, при её отсутствии +7.
const DEFAULT_PHONE_COUNTRY_ID = 1;

const getPairingPhoneCountryId = (
  countryId: number | null | undefined
): number =>
  typeof countryId === 'number' && Number.isFinite(countryId) && countryId > 0
    ? countryId
    : DEFAULT_PHONE_COUNTRY_ID;

/** WhatsApp показывает код сопряжения парами по 4 символа */
const formatPairingCode = (code: string): string =>
  /^[a-z0-9]{8}$/i.test(code) ? `${code.slice(0, 4)}-${code.slice(4)}` : code;

/**
 * Содержимое модалки «Добавить WhatsApp интеграцию»: основной вход по QR
 * (перенесён из Integrations.main как был), запасной — по коду сопряжения
 * (task/10855). QR продолжает обновляться в фоне существующим интервалом.
 */
export const WhatsAppAuthModalContent: FC<WhatsAppAuthModalContentProps> = ({
  qr,
  integrationId,
  activeBranchId,
  phase = WhatsAppAuthPhase.Qr,
  errorMessage,
  onRetry,
}) => {
  const [isCodeMode, setIsCodeMode] = useState(false);
  const [form] = useForm<PhoneFormValues>();
  const { activeBranchCountryId } = useUserPrint();

  const { pairingCode, phone, isCodeLoading, submitPhone, resetPairing } =
    useWhatsAppPairingCode({ integrationId, activeBranchId });

  const backToQr = () => {
    resetPairing();
    setIsCodeMode(false);
  };

  const handlePhoneSubmit = (values: PhoneFormValues) => {
    // Компонент Phone кладёт в форму объект PhoneState — в API уходит только
    // строка номера, иначе CRM отвечает 400 на RequestWhatsAppPairingCode
    // (task/10889).
    if (values.phone?.Phone) {
      submitPhone(values.phone.Phone);
    }
  };

  // Интеграцию перезапустили и ждём, когда она встанет в «Подключается»:
  // раньше этой ветки не было, и человек до 45 секунд читал «наведите телефон
  // на этот экран» поверх пустого места, хотя кода в этой фазе не бывает по
  // определению (issue/11029).
  if (phase === WhatsAppAuthPhase.Preparing) {
    return (
      <div
        className="whatsapp-auth-content whatsapp-auth-content--state"
        data-testid="whatsapp-auth-preparing"
      >
        <Spin />
        <Text strong>Готовим подключение</Text>
        <Text type="secondary">
          Перезапускаем интеграцию — код появится через несколько секунд.
        </Text>
      </div>
    );
  }

  // Код отсканирован, но подтверждения ещё нет. Показывать в этот момент
  // прежний QR нельзя: он уже недействителен, а человек ждёт результата.
  if (phase === WhatsAppAuthPhase.Finishing) {
    return (
      <div
        className="whatsapp-auth-content whatsapp-auth-content--state"
        data-testid="whatsapp-auth-finishing"
      >
        <Spin />
        <Text strong>Код отсканирован. Завершаем подключение</Text>
        <Text type="secondary">
          Это занимает несколько секунд — не закрывайте окно.
        </Text>
      </div>
    );
  }

  if (phase === WhatsAppAuthPhase.Success) {
    return (
      <div
        className="whatsapp-auth-content whatsapp-auth-content--state"
        data-testid="whatsapp-auth-success"
      >
        <CheckCircleFilled className="whatsapp-auth-content__success-icon" />
        <Text strong>Интеграция подключена</Text>
        <Text type="secondary">
          Канал появится в реестре со статусом «Работает».
        </Text>
      </div>
    );
  }

  if (phase === WhatsAppAuthPhase.Error) {
    return (
      <div
        className="whatsapp-auth-content whatsapp-auth-content--state"
        data-testid="whatsapp-auth-error"
      >
        <ExclamationCircleFilled className="whatsapp-auth-content__error-icon" />
        <Text strong>{errorMessage ?? 'Не удалось завершить подключение'}</Text>
        {onRetry && (
          <Button type="primary" onClick={onRetry}>
            Попробовать ещё раз
          </Button>
        )}
      </div>
    );
  }

  if (!isCodeMode) {
    return (
      <div className="whatsapp-auth-content">
        <div>
          1. Откройте WhatsApp на своём телефоне
          <br />
          2. Нажмите &apos;Меню&apos; или &apos;Настройки&apos; и выберите
          &apos;Связанные устройства&apos;
          <br />
          3. Нажмите &apos;Привязка устройства&apos;
          <br />
          4. Наведите свой телефон на этот экран, чтобы считать QR-код
          <br />
        </div>
        {qr?.length ? (
          <div className="qr-container">
            <img
              src={`data:${PNG};base64,${qr}`}
              style={{ width: '264px', alignSelf: 'center' }}
              alt="qr-code"
            />
            <Text type="secondary">
              Если при сканировании QR-кода произошла ошибка, дождитесь
              обновления кода и повторите сканирование.
            </Text>
          </div>
        ) : (
          <Skeleton active />
        )}
        {Boolean(integrationId) && (
          <Link
            className="whatsapp-auth-content__mode-link"
            onClick={() => setIsCodeMode(true)}
            data-testid="whatsapp-pairing-mode-link"
          >
            Не получается отсканировать? Ввести код на телефоне
          </Link>
        )}
      </div>
    );
  }

  if (pairingCode) {
    return (
      <div className="whatsapp-auth-content whatsapp-auth-content__code-stage">
        <Text type="secondary">{`Код для номера ${phone}`}</Text>
        <div
          className="whatsapp-auth-content__code"
          data-testid="whatsapp-pairing-code"
        >
          {formatPairingCode(pairingCode)}
        </div>
        <div>
          1. Откройте WhatsApp на своём телефоне
          <br />
          2. Нажмите &apos;Меню&apos; или &apos;Настройки&apos; и выберите
          &apos;Связанные устройства&apos;
          <br />
          3. Нажмите &apos;Привязка устройства&apos;, затем &apos;Ввести
          код&apos;
          <br />
          4. Введите код с этого экрана — окно закроется само после входа
          <br />
        </div>
        <Link onClick={backToQr}>Вернуться к QR-коду</Link>
      </div>
    );
  }

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={handlePhoneSubmit}
      initialValues={{
        phone: {
          CountryId: getPairingPhoneCountryId(activeBranchCountryId),
          Phone: '',
        },
      }}
      className="whatsapp-auth-content whatsapp-auth-content__phone-form"
    >
      <Item
        label="Номер телефона аккаунта WhatsApp"
        name="phone"
        rules={[{ required: true, message: 'Укажите номер телефона' }]}
      >
        <Phone
          name="phone"
          required
          phoneInputDataTestId="whatsapp-pairing-phone"
        />
      </Item>
      <Text type="secondary">
        Можно вводить как привычно: 8 912… — поймём и поправим сами.
      </Text>
      <div className="whatsapp-auth-content__form-actions">
        <Button
          type="primary"
          htmlType="submit"
          loading={isCodeLoading}
          data-testid="whatsapp-pairing-submit"
        >
          Получить код
        </Button>
        <Link onClick={backToQr}>Вернуться к QR-коду</Link>
      </div>
    </Form>
  );
};
